import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { useConsoleAuth } from "@/contexts/ConsoleAuthContext";
import { toast } from "sonner";

const ConsoleLogin = () => {
  const navigate = useNavigate();
  const { login } = useConsoleAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    const result = await login(form.email, form.password);
    setLoading(false);
    if (result.success) {
      navigate("/console/dashboard");
    } else {
      toast.error(result.error);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F7FB] flex flex-col items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <img
            src="https://customer-assets-eiarnc6j.emergentagent.net/job_bompay-ledger/artifacts/v92480gb_BomPay.png"
            alt="Bompay"
            className="h-12 w-auto object-contain mb-3"
          />
          <p className="text-slate-500 text-sm mt-1">Administrative Management System</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-3xl shadow-xl border border-slate-200 p-8">
          <div className="mb-6">
            <p className="text-slate-400 text-xs font-semibold uppercase tracking-wider mb-1">Sign in</p>
            <h2 className="text-xl font-extrabold text-slate-900">Welcome back</h2>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="text-slate-700 text-sm font-semibold mb-1.5 block">Email address</label>
              <input
                type="email"
                value={form.email}
                onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                placeholder="admin@bompay.ng"
                className="w-full bg-white border border-slate-300 rounded-xl px-4 py-3 text-slate-900 text-sm placeholder-slate-400 focus:outline-none focus:border-[#064BCB] focus:ring-2 focus:ring-[#064BCB]/20 shadow-sm transition-all"
                data-testid="console-login-email"
                autoComplete="email"
              />
            </div>
            <div>
              <label className="text-slate-700 text-sm font-semibold mb-1.5 block">Password</label>
              <div className="relative">
                <input
                  type={showPw ? "text" : "password"}
                  value={form.password}
                  onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                  placeholder="Enter password"
                  className="w-full bg-white border border-slate-300 rounded-xl px-4 py-3 pr-11 text-slate-900 text-sm placeholder-slate-400 focus:outline-none focus:border-[#064BCB] focus:ring-2 focus:ring-[#064BCB]/20 shadow-sm transition-all"
                  data-testid="console-login-password"
                  autoComplete="current-password"
                />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1">
                  {showPw ? <EyeOff size={17} /> : <Eye size={17} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#064BCB] hover:bg-[#0437A0] disabled:opacity-50 text-white font-bold py-3.5 rounded-xl text-sm flex items-center justify-center gap-2 transition-all mt-2 shadow-md shadow-[#064BCB]/25"
              data-testid="console-login-submit"
            >
              {loading ? <><Loader2 size={17} className="animate-spin" />Signing in...</> : "Sign in to Console"}
            </button>
          </form>
        </div>

        <p className="text-center text-slate-400 text-xs mt-6">Bompay Console v1.0 · Restricted Access</p>
      </div>
    </div>
  );
};

export default ConsoleLogin;
